import { useState } from 'react'
import { X, Loader2, Banknote, AlertTriangle, FileDown, Download, CheckCircle2 } from 'lucide-react'
import { ISSUE_LABEL } from '../../lib/scbBulk.js'
import { downloadBlob } from '../../lib/zip.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

const todayISO = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const LEG_TH = { trace: 'ในระบบ', nontrace: 'นอกระบบ' }

/**
 * ดาวน์โหลด (ZIP) — the DF export for one month.
 *
 * Unlike the employee ไฟล์โอนเงิน SCB dialog, nothing is built in the browser
 * here: the zip comes from Xano (`df_doctor/export2`) and the four SCB .txt are
 * appended by /api/salary-doctor/export. So this asks for the value date, then
 * downloads — there is no client-side preview to show first.
 *
 * It fetches the zip as a BLOB rather than pointing a link at the URL, for two
 * reasons. The endpoint answers 502 with JSON when the SCB step fails, and a
 * plain link would render that as raw JSON in a new tab. And the response
 * carries X-Scb-Files / X-Scb-Skipped, which is the only place the caller learns
 * that a doctor was left out of the transfer files — worth showing rather than
 * closing silently on.
 *
 * The value date is asked for because it is the day the money actually leaves
 * and SCB rejects a date in the past; `min` is today for the same reason.
 */
export default function DfTransferModal({ year, month, period, onClose }) {
  const today = todayISO()
  const [valueDate, setValueDate] = useState(today)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState(null)

  const pastDate = valueDate < today

  async function download() {
    if (pastDate || busy) return
    setBusy(true); setErr(''); setResult(null)
    try {
      const q = new URLSearchParams({ year: String(year), month: String(month), value_date: valueDate })
      const res = await fetch(`/api/salary-doctor/export?${q}`, { credentials: 'include' })

      // The failure path answers JSON, the success path a zip.
      const type = res.headers.get('Content-Type') || ''
      if (!res.ok || type.includes('application/json')) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.detail || body?.error || `ดาวน์โหลดไม่สำเร็จ (${res.status})`)
      }

      // `trace/SCB:89:1200804.76` — leg, product group, rows, baht.
      const files = (res.headers.get('X-Scb-Files') || '')
        .split(' ').filter((s) => s && s !== 'none')
        .map((part) => {
          const [group, count, total] = part.split(':')
          const [leg, suffix] = group.split('/')
          return { leg, suffix, count: Number(count), total }
        })
      // `trace/143:unknown_bank` — id, not name: the header must stay ASCII.
      const skipped = (res.headers.get('X-Scb-Skipped') || '')
        .split(' ').filter(Boolean)
        .map((part) => {
          const [group, issue] = part.split(':')
          const [leg, doctorId] = group.split('/')
          return { leg, doctorId, issue }
        })

      downloadBlob(await res.blob(), `DF_${year}-${month}.zip`)

      // Stay open when somebody was left out — that is the one thing here worth
      // reading before the money moves. Otherwise get out of the way.
      if (skipped.length) setResult({ files, skipped })
      else onClose()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 animate-ss-pop-in max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <Banknote size={17} className="text-brand-600" />
            <h2 className="text-[15.5px] font-semibold text-slate-900">ดาวน์โหลดไฟล์รายได้แพทย์</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {!result && (
            <>
              <div className="mb-4">
                <label className={LABEL}>วันที่ต้องการโอนเงิน (Value Date)</label>
                <input
                  type="date" value={valueDate} min={today}
                  onChange={(e) => setValueDate(e.target.value)}
                  className={`${FIELD} ${pastDate ? 'border-rose-300' : ''}`}
                />
                {pastDate
                  ? <p className="text-[12px] text-rose-500 mt-1">วันที่โอนต้องไม่ย้อนหลัง — ธนาคารจะปฏิเสธไฟล์</p>
                  : <p className="text-[12px] text-slate-400 mt-1">รอบ {period}</p>}
              </div>

              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 px-4 py-3 text-[12.5px] text-slate-600 space-y-1">
                <div className="font-medium text-slate-700 mb-1">ในซิปจะมี</div>
                <div>• CSV 3 ไฟล์ — สรุป DF, trace, nontrace</div>
                <div>• ไฟล์โอนเงิน SCB 4 ไฟล์ (.txt) — ในระบบ/นอกระบบ × ไทยพาณิชย์/ธนาคารอื่น</div>
                <div className="text-slate-400 pt-1">ทุกไฟล์ตัดเงินจากบัญชีบริษัทของสาขาอารีย์</div>
              </div>
            </>
          )}

          {err && (
            <div className="mt-3 rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-3 text-[13px] text-rose-700">
              {err}
            </div>
          )}

          {result && (
            <>
              <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-emerald-700 mb-3">
                <CheckCircle2 size={15} /> ดาวน์โหลดแล้ว
              </div>

              <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden mb-4">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-slate-50 text-slate-400">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">ไฟล์โอนเงิน</th>
                      <th className="text-right font-medium px-3 py-2">คน</th>
                      <th className="text-right font-medium px-3 py-2">ยอดโอน</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {result.files.map((f) => (
                      <tr key={`${f.leg}-${f.suffix}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5 text-slate-700">
                            <FileDown size={13} className="text-slate-300 shrink-0" />
                            <span className="truncate">{LEG_TH[f.leg] || f.leg} · {f.suffix === 'SCB' ? 'ไทยพาณิชย์' : 'ธนาคารอื่น'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{f.count}</td>
                        <td className="px-3 py-2 text-right font-medium text-slate-800 tabular-nums">{f.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-amber-800 mb-1.5">
                  <AlertTriangle size={14} /> ไม่ได้อยู่ในไฟล์โอนเงิน {result.skipped.length} คน
                </div>
                <ul className="text-[12.5px] text-amber-800/90 space-y-0.5">
                  {result.skipped.map((s, i) => (
                    <li key={i}>
                      หมอ #{s.doctorId} ({LEG_TH[s.leg] || s.leg}) — {ISSUE_LABEL[s.issue] || s.issue}
                    </li>
                  ))}
                </ul>
                <p className="text-[12px] text-amber-800/80 mt-2">
                  รายชื่อเต็มอยู่ในไฟล์ <span className="font-medium">DF_{year}-{month} skipped.txt</span> ในซิป ·
                  ยอดของทุกคนยังอยู่ในไฟล์ CSV และยังต้องจ่าย
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          {result ? (
            <button onClick={onClose}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
              เข้าใจแล้ว
            </button>
          ) : (
            <>
              <button onClick={onClose}
                className="px-3.5 py-2 rounded-xl text-[13px] font-medium text-slate-600 hover:bg-slate-100">
                ยกเลิก
              </button>
              <button onClick={download} disabled={busy || pastDate}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={15} />}
                {busy ? 'กำลังสร้างไฟล์…' : 'ดาวน์โหลด (ZIP)'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
